import { graphql, useStaticQuery } from 'gatsby';
import { ContentfulMetaSeo } from 'graphqlTypes';
import { Helmet } from 'react-helmet';

import defaultSeoImage from 'assets/images/banner.jpeg';

import useFreshworks from 'utils/useFreshworks.js';

import type { FC } from 'react';

interface SiteMetaData {
  site: {
    siteMetadata: {
      title: string;
      siteDescription: string;
      siteUrl: string;
      siteLanguage: string;
    };
  };
}

interface SeoProps extends ContentfulMetaSeo {
  slug: string;
  productSchemaStringified?: string;
  ogFeaturedImage?: string;
}

const SEO: FC<SeoProps> = ({
  pageTitle,
  pageDescription,
  ogFeaturedImage,
  openGraphImage,
  indexibility,
  follow,
  slug,
  canonicalUrl,
  productSchemaStringified,
}) => {
  const {
    site: { siteMetadata },
  } = useStaticQuery<SiteMetaData>(graphql`
    query siteMetaData {
      site {
        siteMetadata {
          title
          siteDescription
          siteUrl
          siteLanguage
        }
      }
    }
  `);

  const cleanTitle = pageTitle ? pageTitle.replace('| FieldRoutes', '').replace('| Field Routes', '') : undefined;
  const seoTitle = cleanTitle ? `${cleanTitle} | ${siteMetadata.title}` : siteMetadata.title;
  const seoDescription = pageDescription?.pageDescription || siteMetadata.siteDescription;
  const seoImage = openGraphImage?.file?.url || ogFeaturedImage || defaultSeoImage;
  const seoUrl = slug ? `${siteMetadata.siteUrl}${slug}` : siteMetadata.siteUrl;
  const canonical = canonicalUrl || seoUrl;
  useFreshworks();

  return (
    <Helmet>
      <html lang={siteMetadata.siteLanguage} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="application-name" content={seoTitle} />
      <meta name="robots" content={`${!indexibility ? 'noindex' : 'index'}, ${!follow ? 'nofollow' : 'follow'}`} />

      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content={siteMetadata.title} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@fieldroutes" />
      <meta name="twitter:url" content={seoUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      {productSchemaStringified && <script type="application/ld+json">{productSchemaStringified}</script>}
      <meta data-react-helmet="true" name="facebook-domain-verification" content="t0j6ii9hc6yeuvwg9pgeysnn7j28uv" />
      <script src="https://js.hsforms.net/forms/embed/v2.js" />
      <script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/2731810.js"></script>
    </Helmet>
  );
};

export default SEO;
